import React from 'react'
import { Navigate, Outlet, useLocation } from 'react-router'
import { useCurrentUser } from '@/features/auth/hooks/useCurrentUser'
import type { RouteAuth } from '../router/route-definitions'
import { useAuthStore } from '@/features/auth/store'

interface AuthGuardProps {
  children?: React.ReactElement
  auth?: RouteAuth
}

const AuthGuard: React.FC<AuthGuardProps> = ({ children, auth = {} }) => {
  const location = useLocation()
  const isAuthenticated = useAuthStore(state => state.isAuthenticated)
  const user = useAuthStore(state => state.user)
  const isLoading = useAuthStore(state => state.isLoading)
  const accessToken = useAuthStore(state => state.accessToken)
  const { data: currentUser, isFetching, isPending } = useCurrentUser()

  const effectiveUser = currentUser ?? user ?? undefined
  const resolvedLineId = (() => {
    if (!effectiveUser) return undefined
    if ('lineInfo' in effectiveUser) {
      return effectiveUser.lineInfo?.lineUserId
    }
    return effectiveUser.lineId
  })()
  const isChecking = isLoading || (Boolean(accessToken) && (isPending || isFetching))

  // Loading state
  if (isChecking) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <div>Loading...</div>
      </div>
    )
  }

  // Check auth requirements based on metadata
  const { requiresAuth, publicOnly, requiresLineId } = auth

  // 1. Public only routes (login, signup)
  if (publicOnly && isAuthenticated) {
    const searchParams = new URLSearchParams(location.search)
    const redirectUrl = searchParams.get('redirect')
    const targetUrl = redirectUrl ? decodeURIComponent(redirectUrl) : '/'
    return <Navigate to={targetUrl} replace />
  }

  // 2. Routes that require authentication
  if (requiresAuth && !isAuthenticated) {
    const redirectUrl = encodeURIComponent(location.pathname + location.search)
    return <Navigate to={`/auth/signin?redirect=${redirectUrl}`} replace />
  }

  // 3. Routes that require LINE ID (only check if authenticated)
  if (isAuthenticated && requiresLineId && !resolvedLineId) {
    // Don't redirect if already on line validation page
    if (location.pathname !== '/auth/line-validation') {
      return <Navigate to="/auth/line-validation" replace />
    }
  }

  // 4. Default behavior for authenticated users without LINE ID
  // (All authenticated users need LINE ID except on the validation page itself)
  if (
    isAuthenticated &&
    !resolvedLineId &&
    location.pathname !== '/auth/line-validation' &&
    !publicOnly
  ) {
    return <Navigate to="/auth/line-validation" replace />
  }

  // All checks passed, render the route
  return children || <Outlet />
}

export default AuthGuard
